import {
  useBillDaysThresholdList,
  useBillDaysThresholdSummary,
} from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  FileText,
  XCircle,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BillDaysThresholdFilters,
  BillDaysThresholdFiltersType,
} from "./BillDaysThresholdFilters";

// Interface for Bill Days data
interface BillDaysThreshold {
  id: string | number;
  accountNumber: string;
  consumerNumber: string;
  previousReading: number;
  previousReadingDate: string;
  currentReading: number;
  currentReadingDate: string;
  serviceDays: number;
  thresholdCheck: "critical" | "moderate" | "normal" | "extended";
}

interface BillDaysThresholdTabProps {
  creditNoteEnabled: boolean;
  onCreditNoteChange: (value: boolean) => void;
}

export const BillDaysThresholdTab: React.FC<BillDaysThresholdTabProps> = ({
  creditNoteEnabled,
  onCreditNoteChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<BillDaysThresholdFiltersType>({
    threshold_check: "all",
  });
  const { cycleId } = useParams();

  const itemsPerPage = PAGE_SIZE;

  const { data: summary } = useBillDaysThresholdSummary(cycleId);

  // Build filter object for API call
  const filterParams = useMemo(() => {
    const params: Record<string, any> = {
      page: currentPage,
      limit: itemsPerPage,
    };

    if (searchTerm.trim()) {
      params.search = searchTerm;
    }

    if (filters.threshold_check && filters.threshold_check !== "all") {
      params.threshold_check = filters.threshold_check;
    }

    return params;
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const { data: billDaysList, isLoading } = useBillDaysThresholdList(
    cycleId,
    filterParams.page,
    filterParams.limit,
    filterParams.threshold_check
  );

  // Calculate KPI metrics
  const kpiMetrics = useMemo(() => {
    const critical = summary?.lessThanFifteen?.count || 0;
    const moderate = summary?.fifteenToThirty?.count || 0;
    const normal = summary?.thirtyToSixty?.count || 0;
    const extended = summary?.aboveSixty?.count || 0;

    return { critical, moderate, normal, extended };
  }, [summary]);

  // Pagination calculations
  const totalItems = billDaysList?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to page 1 on search
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: BillDaysThresholdFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 on filter change
  };

  // Handle apply filters
  const handleApplyFilters = (newFilters: BillDaysThresholdFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 on filter apply
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      threshold_check: "all",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Get threshold badge configuration
  const getThresholdBadgeConfig = (threshold: string) => {
    switch (threshold) {
      case "critical":
        return {
          label: "Less than 15 days",
          className: "bg-blue-100 text-blue-800 border-blue-200",
        };
      case "moderate":
        return {
          label: "15-30 days",
          className: "bg-yellow-100 text-yellow-800 border-yellow-200",
        };
      case "normal":
        return {
          label: "30-60 days",
          className: "bg-green-100 text-green-800 border-green-200",
        };
      case "extended":
        return {
          label: "60+ days",
          className: "bg-red-100 text-red-800 border-red-200",
        };
      default:
        return {
          label: threshold,
          className: "bg-gray-100 text-gray-800 border-gray-200",
        };
    }
  };

  // Define table columns
  const columns: TableColumn<BillDaysThreshold>[] = [
    {
      key: "accountNo",
      header: "Account Number",
    },
    {
      key: "consumerNumber",
      header: "Consumer Number",
    },
    {
      key: "previousReading",
      header: "Previous Reading",
    },
    {
      key: "previousReadingDate",
      header: "Previous Reading Date",
    },
    {
      key: "currentReading",
      header: "Current Reading",
    },
    {
      key: "currentReadingDate",
      header: "Current Reading Date",
    },
    {
      key: "serviceDays",
      header: "Service Days",
    },
    {
      key: "thresholdCheck",
      header: "Threshold Check",
      type: "custom",
      sortable: false,
      render: (value: string) => {
        const config = getThresholdBadgeConfig(value);
        return (
          <span
            className={`inline-flex px-3 py-1 rounded-md border text-xs font-medium ${config.className}`}
          >
            {config.label}
          </span>
        );
      },
    },
  ];

  // KPI Cards data
  const kpiCards = [
    {
      title: "Less than 15 Days",
      count: kpiMetrics.critical,
      trendSummary: "Billing period significantly shorter than standard cycle",
      icon: <AlertTriangle className="h-6 w-6 text-blue-600" />,
      badge: "Critical",
      badgeColor: "bg-blue-100 text-blue-800 border-blue-300",
      cardColor: "bg-blue-50 border-blue-200",
      titleColor: "text-blue-700",
      countColor: "text-blue-900",
      descColor: "text-blue-800",
    },
    {
      title: "15-30 Days",
      count: kpiMetrics.moderate,
      trendSummary: "Below standard billing cycle, requires attention",
      icon: <AlertCircle className="h-6 w-6 text-yellow-600" />,
      badge: "Moderate",
      badgeColor: "bg-yellow-100 text-yellow-800 border-yellow-300",
      cardColor: "bg-yellow-50 border-yellow-200",
      titleColor: "text-yellow-700",
      countColor: "text-yellow-900",
      descColor: "text-yellow-800",
    },
    {
      title: "30-60 Days",
      count: kpiMetrics.normal,
      trendSummary: "Within acceptable billing cycle range",
      icon: <CheckCircle className="h-6 w-6 text-green-600" />,
      badge: "Normal",
      badgeColor: "bg-green-100 text-green-800 border-green-300",
      cardColor: "bg-green-50 border-green-200",
      titleColor: "text-green-700",
      countColor: "text-green-900",
      descColor: "text-green-800",
    },
    {
      title: "60+ Days",
      count: kpiMetrics.extended,
      trendSummary: "Exceeds standard cycle, immediate review needed",
      icon: <XCircle className="h-6 w-6 text-red-600" />,
      badge: "Extended",
      badgeColor: "bg-red-100 text-red-800 border-red-300",
      cardColor: "bg-red-50 border-red-200",
      titleColor: "text-red-700",
      countColor: "text-red-900",
      descColor: "text-red-800",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Bill Days Threshold Check
        </h2>
        <p className="text-sm text-gray-600">
          Review service days between meter readings and identify billing period
          anomalies
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            icon={card.icon}
            badge={card.badge}
            badgeColor={card.badgeColor}
          />
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            <h3 className="text-sm font-semibold text-gray-900">
              Create Credit Note for Over-Billed Consumers
            </h3>
          </div>

          <Switch
            checked={creditNoteEnabled}
            onCheckedChange={onCreditNoteChange}
          />
        </div>

        {/* Message */}
        <div
          className={`mt-4 rounded-lg border p-4 text-sm ${
            creditNoteEnabled
              ? "border-purple-200 bg-purple-50 text-purple-700"
              : "border-yellow-300 bg-yellow-50 text-yellow-800"
          }`}
        >
          {creditNoteEnabled ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle className="w-4 h-4" />
                Credit notes will be automatically generated
              </div>
              <p>
                Credit notes will be created for all over-billed consumers to
                refund the excess amount charged. These credits will be applied
                to their next billing cycle.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="w-4 h-4" />
                Credit note generation is disabled
              </div>
              <p>
                Over-billed consumers will not receive automatic credit notes.
                You will need to manually process refunds or adjustments for
                these consumers.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Table Section with Filters */}
      <div className="space-y-4">
        <GenericTable
          data={billDaysList?.result || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          selectable={false}
          searchPlaceholder="Search by account or consumer number..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          filterPanel={
            <BillDaysThresholdFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
          }
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
          emptyMessage="No billing threshold data found"
        />
      </div>
    </div>
  );
};
